import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.1 Tier 3 follow-up — "New Transfer" modal on the audit page.
// Unlike the tree modal (which pre-fills source from the line you clicked),
// this one asks the user to pick both source and target from dropdowns.
// On save we reload the page so the new row shows up in the audit list —
// the audit page re-runs its filters server-side, which is simpler than
// trying to insert the row client-side without knowing where it sorts.
export default class extends Controller {
  static targets = [
    "modal",
    "sourceSelect", "sourceBalance",
    "targetSelect",
    "amountInput", "reasonInput"
  ]

  static values = {
    projectId: Number,
    budgetId: Number,
    currency: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
  }

  // Read a flash message stashed in sessionStorage by a prior action
  // (create or delete on this page). Also covers flashes set by the
  // sibling `budget-transfer-delete` controller since that one doesn't
  // have a connect() of its own.
  _showPendingFlash() {
    const raw = sessionStorage.getItem("budget_flash")
    if (!raw) return
    sessionStorage.removeItem("budget_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._toast(message, type)
    } catch { /* ignore */ }
  }

  show() {
    this.sourceSelectTarget.value = ""
    this.targetSelectTarget.value = ""
    this.amountInputTarget.value = ""
    this.reasonInputTarget.value = ""
    this.sourceBalanceTarget.textContent = "—"
    this.amountInputTarget.removeAttribute("max")
    // Re-enable all target options before showing
    Array.from(this.targetSelectTarget.options).forEach(opt => { opt.disabled = false })
    this.modalTarget.classList.remove("hidden")
  }

  hide() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) { event.stopPropagation() }

  onSourceChange() {
    const opt = this.sourceSelectTarget.selectedOptions[0]
    const balance = opt ? parseFloat(opt.dataset.balance || "0") : 0
    this.sourceBalanceTarget.textContent = opt && opt.value
      ? this._formatCurrency(balance)
      : "—"
    if (opt && opt.value) {
      this.amountInputTarget.max = balance.toFixed(2)
    } else {
      this.amountInputTarget.removeAttribute("max")
    }
    // Disable the same line in the target picker so the user can't self-transfer
    const sourceId = opt ? opt.value : ""
    Array.from(this.targetSelectTarget.options).forEach(o => {
      o.disabled = (o.value && o.value === sourceId)
    })
    // If target was already set to the same line, clear it
    if (this.targetSelectTarget.value === sourceId) {
      this.targetSelectTarget.value = ""
    }
  }

  async save() {
    const sourceId = parseInt(this.sourceSelectTarget.value, 10)
    const targetId = parseInt(this.targetSelectTarget.value, 10)
    if (!sourceId) { this._toast("Pick a source line", "error"); return }
    if (!targetId) { this._toast("Pick a target line", "error"); return }
    if (sourceId === targetId) { this._toast("Source and target must differ", "error"); return }

    const amount = parseFloat(this.amountInputTarget.value)
    if (!amount || amount <= 0) { this._toast("Enter an amount greater than zero", "error"); return }

    const sourceOpt = this.sourceSelectTarget.selectedOptions[0]
    const sourceBalance = sourceOpt ? parseFloat(sourceOpt.dataset.balance || "0") : 0
    if (amount > sourceBalance + 0.001) {
      this._toast("Amount exceeds the source line's approved balance", "error")
      return
    }

    const resp = await fetch("/budget_transfers", {
      method: "POST",
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        budget_transfer: {
          source_line_item_id: sourceId,
          target_line_item_id: targetId,
          amount,
          reason: this.reasonInputTarget.value
        }
      })
    })

    let body = null
    try { if (resp.headers.get("content-type")?.includes("application/json")) body = await resp.json() } catch {}

    if (!resp.ok) {
      this._toast(body?.errors?.join(", ") || "Failed to record transfer", "error")
      return
    }

    try {
      mixpanelTrack("Budget Transfer Created", {
        budget_transfer_id: body?.id,
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        amount,
        from_contingency: !!body?.from_contingency,
        via: "audit_page"
      })
      if (body?.from_contingency) {
        mixpanelTrack("Contingency Drawn", {
          budget_transfer_id: body?.id,
          project_id: this.projectIdValue,
          budget_id: this.budgetIdValue,
          amount
        })
      }
    } catch { /* mixpanel optional */ }

    sessionStorage.setItem("budget_flash", JSON.stringify({ message: "Transfer created", type: "success" }))
    window.location.reload()
  }

  _formatCurrency(n) {
    const sym = this.hasCurrencyValue && this.currencyValue ? this.currencyValue : "$"
    const fixed = Number(n || 0).toFixed(2)
    const [intPart, fracPart] = fixed.split(".")
    const withDelim = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return `${sym}${withDelim}.${fracPart}`
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
